/**
 * Component 7: Indicator Trend
 * Interactive bar charts with target lines, color coding, manual data entry
 * Auto-generates charts based on outcomes/factors from Components 2 & 3
 */

import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Cell
} from 'recharts';
import {
  Plus as PlusIcon,
  TrendingUp as TrendingUpIcon,
  Calendar as CalendarIcon,
  Target as TargetIcon,
  Edit3 as EditIcon,
  Trash2 as TrashIcon,
  Info as InfoIcon
} from 'lucide-react';

import type { 
  KATABoardV2, 
  Outcome, 
  Factor, 
  IndicatorDataPoint, 
  IndicatorDataPointCreate 
} from '../../../types/kataBoardV2';
import { outcomeApi, indicatorDataPointApi, factorApi } from '../../../services/kataBoardV2';
import { useKataBoardCanEdit } from '../../../contexts/KataBoardAccessContext';
// import { useAlert } from '../../../hooks/useAlert';

interface Component07Props {
  kataBoard: KATABoardV2;
}

interface ChartData {
  date: string;
  value: number;
  isOnTarget: boolean;
  notes?: string;
  // Time identifiers for gap detection
  dateValue?: string;         // ISO date string for daily
  week_number?: number;       // Week number for weekly
  month_name?: string;        // Month name for monthly
  year?: number;              // Year
}

interface WaterfallDataPoint {
  label: string;              // Time identifier
  value: number;              // Absolute value
  delta?: number;             // Change from previous (absolute)
  deltaPercent?: number;      // Change from previous (percentage)
  isPositive: boolean;        // Whether change is positive (good)
  type: 'start' | 'change' | 'end' | 'gap';  // Bar type
  previousValue?: number;     // Previous point value (for tooltip)
  isGap?: boolean;            // Whether this is a gap marker
  gapLabel?: string;          // Label for gap period
}

type DisplayMode = 'daily' | 'weekly' | 'monthly';

const Component07_IndicatorTrend: React.FC<Component07Props> = ({ kataBoard }) => {
  const canEdit = useKataBoardCanEdit();
  // const { confirmDelete, AlertComponent } = useAlert();
  
  // State management
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);
  const [factors, setFactors] = useState<Factor[]>([]);
  const [selectedFactor, setSelectedFactor] = useState<Factor | null>(null);
  const [dataPoints, setDataPoints] = useState<IndicatorDataPoint[]>([]);
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [chartDataResponse, setChartDataResponse] = useState<any>(null); // Store full response for waterfall chart
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [displayMode, setDisplayMode] = useState<DisplayMode>('daily');
  
  // Data entry form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPoint, setEditingPoint] = useState<IndicatorDataPoint | null>(null);
  const [formData, setFormData] = useState({
    date: '',
    week_number: '',
    year: new Date().getFullYear().toString(),
    month_name: '',
    value: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Get indicator frequency from workshop
  const indicatorFrequency = kataBoard.workshop_info?.indicator_frequency || 'daily';
  const hasIndicatorFrequency = !!kataBoard.workshop_info?.indicator_frequency;

  // Get allowed display modes based on indicator_frequency
  const getAllowedDisplayModes = (): DisplayMode[] => {
    if (!indicatorFrequency) return ['daily'];
    
    switch (indicatorFrequency) {
      case 'daily':
        return ['daily', 'weekly', 'monthly'];
      case 'weekly':
        return ['weekly', 'monthly'];
      case 'monthly':
        return ['monthly'];
      default:
        return ['daily'];
    }
  };

  const allowedModes = getAllowedDisplayModes();

  // Set initial display mode to match indicator_frequency
  useEffect(() => {
    if (indicatorFrequency) {
      setDisplayMode(indicatorFrequency as DisplayMode);
    }
  }, [indicatorFrequency]);
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Load outcomes and factors from Components 2 & 3 (use data already in kataBoard)
  useEffect(() => {
    try {
      setLoading(true);
      setError(null);
      
      // Filter outcomes for current condition (same as Components 2 & 3)
      const currentConditionOutcomes = kataBoard.outcomes?.filter(
        outcome => outcome.outcome_type === 'current'
      ) || [];
      
      // Collect all factors from all outcomes
      const allFactors: Factor[] = [];
      currentConditionOutcomes.forEach(outcome => {
        if (outcome.factors && outcome.factors.length > 0) {
          // Add outcome context to factors for display
          outcome.factors.forEach(factor => {
            allFactors.push({
              ...factor,
              outcome_name: outcome.name,
              outcome_id: outcome.id,
              outcome_kpi_type: outcome.kpi_type,
              outcome_target_value: outcome.target_value
            } as Factor & { 
              outcome_name: string; 
              outcome_id: number; 
              outcome_kpi_type: string;
              outcome_target_value: number | null;
            });
          });
        }
      });
      
      console.log('Loading outcomes and factors:', currentConditionOutcomes.length, 'outcomes,', allFactors.length, 'factors');
      setOutcomes(currentConditionOutcomes);
      setFactors(allFactors);
      
      // Auto-select first factor if available
      if (allFactors.length > 0) {
        setSelectedFactor(allFactors[0]);
      } else {
        setSelectedFactor(null);
      }
    } catch (error) {
      console.error('Failed to load outcomes and factors:', error);
      setError('Failed to load outcomes and factors');
      setOutcomes([]);
      setFactors([]);
    } finally {
      setLoading(false);
    }
  }, [kataBoard.outcomes]);

  // Load data points for selected factor
  useEffect(() => {
    const loadDataPoints = async () => {
      if (!selectedFactor) {
        setDataPoints([]);
        setChartData([]);
        return;
      }
      
      try {
        setLoading(true);
        // Load raw data points for table
        const dataPointsData = await indicatorDataPointApi.getAll(undefined, selectedFactor.id);
        setDataPoints(Array.isArray(dataPointsData) ? dataPointsData : []);
        
        // Load chart data with display mode
        const chartDataResponse = await indicatorDataPointApi.getChartData(
          (selectedFactor as any).outcome_id,
          undefined,
          displayMode,
          selectedFactor.id
        );
        
        // Transform chart data
        const transformedData: ChartData[] = chartDataResponse.data_points.map((point: any) => ({
          date: point.time_identifier || point.date || '',
          value: parseFloat(point.value.toString()),
          isOnTarget: point.is_on_target || false,
          notes: point.notes || ''
        }));
        
        setChartData(transformedData);
        
        // Store chart data response for waterfall chart (includes factor.kpi_type)
        setChartDataResponse(chartDataResponse);
      } catch (error) {
        console.error('Failed to load data points:', error);
        setError('Failed to load data points');
        setDataPoints([]);
        setChartData([]);
        setChartDataResponse(null);
      } finally {
        setLoading(false);
      }
    };
    
    loadDataPoints();
  }, [selectedFactor, indicatorFrequency, displayMode]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFactor || !formData.value) return;

    // Validate based on frequency
    if (indicatorFrequency === 'daily' && !formData.date) {
      setError('Date is required for daily frequency');
      return;
    }
    if (indicatorFrequency === 'weekly' && (!formData.week_number || !formData.year)) {
      setError('Week number and year are required for weekly frequency');
      return;
    }
    if (indicatorFrequency === 'monthly' && (!formData.month_name || !formData.year)) {
      setError('Month and year are required for monthly frequency');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const dataPointData: IndicatorDataPointCreate = {
        factor: selectedFactor.id,
        value: parseFloat(formData.value),
        notes: formData.notes || undefined
      };

      // Add time identifier based on frequency
      if (indicatorFrequency === 'daily') {
        dataPointData.date = formData.date;
      } else if (indicatorFrequency === 'weekly') {
        const weekNum = parseInt(formData.week_number);
        if (weekNum < 1 || weekNum > 52) {
          setError('Week number must be between 1 and 52');
          return;
        }
        dataPointData.week_number = weekNum;
        dataPointData.year = parseInt(formData.year);
      } else if (indicatorFrequency === 'monthly') {
        dataPointData.month_name = formData.month_name;
        dataPointData.year = parseInt(formData.year);
      }

      if (editingPoint) {
        // Update existing data point
        await indicatorDataPointApi.update(editingPoint.id, dataPointData);
      } else {
        // Create new data point
        await indicatorDataPointApi.create(dataPointData);
      }

      // Reload data points
      const updatedDataPoints = await indicatorDataPointApi.getAll(undefined, selectedFactor.id);
      setDataPoints(Array.isArray(updatedDataPoints) ? updatedDataPoints : []);
      
      const transformedData: ChartData[] = updatedDataPoints.map(point => ({
        date: point.date || (point as any).time_identifier || '',
        value: parseFloat(point.value.toString()),
        isOnTarget: point.is_on_target || false,
        notes: point.notes || ''
      }));
      
      setChartData(transformedData.sort((a, b) => {
        // Sort by date if available, otherwise by string
        if (a.date && b.date) {
          return new Date(a.date).getTime() - new Date(b.date).getTime();
        }
        return a.date.localeCompare(b.date);
      }));

      // Reset form
      setFormData({ 
        date: '', 
        week_number: '',
        year: new Date().getFullYear().toString(),
        month_name: '',
        value: '', 
        notes: '' 
      });
      setShowAddForm(false);
      setEditingPoint(null);
    } catch (error: any) {
      console.error('Failed to save data point:', error);
      setError(error.response?.data?.error || error.message || 'Failed to save data point');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete data point
  const handleDelete = async (dataPoint: IndicatorDataPoint) => {
    if (!canEdit) return;
    const timeIdentifier = dataPoint.date || 
      (dataPoint.week_number && dataPoint.year ? `Week ${dataPoint.week_number}, ${dataPoint.year}` : '') ||
      (dataPoint.month_name && dataPoint.year ? `${dataPoint.month_name} ${dataPoint.year}` : '') ||
      'this data point';
    
    const confirmed = window.confirm(
      `Are you sure you want to delete the data point for ${timeIdentifier}?`
    );

    if (!confirmed || !selectedFactor) return;

    try {
      await indicatorDataPointApi.delete(dataPoint.id);
      
      // Reload data points
      const updatedDataPoints = await indicatorDataPointApi.getAll(undefined, selectedFactor.id);
      setDataPoints(Array.isArray(updatedDataPoints) ? updatedDataPoints : []);
      
      const transformedData: ChartData[] = updatedDataPoints
        .map(point => ({
          date: point.date || '',
          value: parseFloat(point.value.toString()),
          isOnTarget: point.is_on_target || false,
          notes: point.notes || ''
        }))
        .filter(point => point.date !== ''); // Filter out points without dates
      
      setChartData(transformedData.sort((a, b) => {
        if (!a.date || !b.date) return 0;
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      }));
    } catch (error) {
      console.error('Failed to delete data point:', error);
      setError('Failed to delete data point');
    }
  };

  // Handle edit data point
  const handleEdit = async (dataPoint: IndicatorDataPoint) => {
    if (!canEdit || !dataPoint.id) return;
    
    try {
      // Fetch full data point to get all fields
      const fullDataPoint = await indicatorDataPointApi.getById(dataPoint.id);
      setEditingPoint(fullDataPoint);
      
      if (indicatorFrequency === 'daily') {
        setFormData({
          date: fullDataPoint.date || '',
          week_number: '',
          year: new Date().getFullYear().toString(),
          month_name: '',
          value: fullDataPoint.value?.toString() || '',
          notes: fullDataPoint.notes || ''
        });
      } else if (indicatorFrequency === 'weekly') {
        setFormData({
          date: '',
          week_number: fullDataPoint.week_number?.toString() || '',
          year: fullDataPoint.year?.toString() || new Date().getFullYear().toString(),
          month_name: '',
          value: fullDataPoint.value?.toString() || '',
          notes: fullDataPoint.notes || ''
        });
      } else if (indicatorFrequency === 'monthly') {
        setFormData({
          date: '',
          week_number: '',
          year: fullDataPoint.year?.toString() || new Date().getFullYear().toString(),
          month_name: fullDataPoint.month_name || '',
          value: fullDataPoint.value?.toString() || '',
          notes: fullDataPoint.notes || ''
        });
      }
      
      setShowAddForm(true);
    } catch (err: any) {
      console.error('Failed to load data point for editing:', err);
      setError(err.message || 'Failed to load data point');
    }
  };

  // Detect gaps in time series data
  const detectGaps = (
    dataPoints: ChartData[],
    displayMode: DisplayMode
  ): { index: number; gapLabel: string }[] => {
    if (dataPoints.length < 2) return [];
    
    const gaps: { index: number; gapLabel: string }[] = [];
    
    for (let i = 1; i < dataPoints.length; i++) {
      const current = dataPoints[i];
      const previous = dataPoints[i - 1];
      let isGap = false;
      let gapLabel = '';
      
      if (displayMode === 'daily' && current.dateValue && previous.dateValue) {
        // Check for gaps in daily data
        const currentDate = new Date(current.dateValue);
        const previousDate = new Date(previous.dateValue);
        const daysDiff = Math.floor((currentDate.getTime() - previousDate.getTime()) / (1000 * 60 * 60 * 24));
        
        if (daysDiff > 1) {
          isGap = true;
          const gapStart = new Date(previousDate);
          gapStart.setDate(gapStart.getDate() + 1);
          const gapEnd = new Date(currentDate);
          gapEnd.setDate(gapEnd.getDate() - 1);
          
          if (daysDiff === 2) {
            gapLabel = gapStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          } else {
            gapLabel = `${gapStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${gapEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
          }
        }
      } else if (displayMode === 'weekly' && current.week_number && previous.week_number && current.year && previous.year) {
        // Check for gaps in weekly data
        const currentWeek = current.week_number + (current.year * 100);
        const previousWeek = previous.week_number + (previous.year * 100);
        const weeksDiff = currentWeek - previousWeek;
        
        if (weeksDiff > 1) {
          isGap = true;
          if (weeksDiff === 2) {
            const gapWeek = previous.week_number! + 1;
            const gapYear = gapWeek > 52 ? previous.year! + 1 : previous.year!;
            const gapWeekNum = gapWeek > 52 ? 1 : gapWeek;
            gapLabel = `Week ${gapWeekNum}, ${gapYear}`;
          } else {
            gapLabel = `${weeksDiff - 1} weeks missing`;
          }
        }
      } else if (displayMode === 'monthly' && current.month_name && previous.month_name && current.year && previous.year) {
        // Check for gaps in monthly data
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                          'July', 'August', 'September', 'October', 'November', 'December'];
        const currentMonth = monthNames.indexOf(current.month_name) + 1 + (current.year * 100);
        const previousMonth = monthNames.indexOf(previous.month_name) + 1 + (previous.year * 100);
        const monthsDiff = currentMonth - previousMonth;
        
        if (monthsDiff > 1) {
          isGap = true;
          if (monthsDiff === 2) {
            const gapMonthIndex = monthNames.indexOf(previous.month_name!) + 1;
            const gapMonth = gapMonthIndex === 12 ? 1 : gapMonthIndex + 1;
            const gapYear = gapMonthIndex === 12 ? previous.year! + 1 : previous.year!;
            gapLabel = `${monthNames[gapMonth - 1]} ${gapYear}`;
          } else {
            gapLabel = `${monthsDiff - 1} months missing`;
          }
        }
      }
      
      if (isGap) {
        gaps.push({ index: i, gapLabel });
      }
    }
    
    return gaps;
  };

  // Transform chart data to waterfall format with gap detection
  const transformToWaterfallData = (
    dataPoints: ChartData[],
    kpiType: 'increase' | 'decrease',
    displayMode: DisplayMode
  ): WaterfallDataPoint[] => {
    if (dataPoints.length === 0) return [];
    
    const waterfall: WaterfallDataPoint[] = [];
    
    // Detect gaps
    const gaps = detectGaps(dataPoints, displayMode);
    const gapIndices = new Set(gaps.map(g => g.index));
    
    // Starting point (first data point)
    waterfall.push({
      label: dataPoints[0].date,
      value: dataPoints[0].value,
      type: 'start',
      isPositive: true
    });
    
    // Calculate changes between consecutive points
    for (let i = 1; i < dataPoints.length; i++) {
      // Insert gap marker if there's a gap before this point
      if (gapIndices.has(i)) {
        const gap = gaps.find(g => g.index === i);
        waterfall.push({
          label: gap?.gapLabel || 'Gap',
          value: dataPoints[i - 1].value, // Keep previous value for visualization
          type: 'gap',
          isPositive: true,
          isGap: true,
          gapLabel: gap?.gapLabel || 'Gap',
          previousValue: dataPoints[i - 1].value
        });
      }
      
      const current = dataPoints[i];
      const previous = dataPoints[i - 1];
      const delta = current.value - previous.value;
      
      // Calculate percentage change
      // Handle division by zero (previous value = 0)
      const deltaPercent = previous.value !== 0 
        ? (delta / previous.value) * 100 
        : (delta !== 0 ? (delta > 0 ? Infinity : -Infinity) : 0);
      
      // Determine if change is positive based on KPI type
      let isPositive: boolean;
      if (kpiType === 'increase') {
        // For increase type: higher is better, so increase is positive
        isPositive = delta >= 0;
      } else {
        // For decrease type: lower is better, so decrease is positive
        isPositive = delta <= 0;
      }
      
      // Mark last point as 'end', others as 'change'
      const pointType: 'change' | 'end' = i === dataPoints.length - 1 ? 'end' : 'change';
      
      waterfall.push({
        label: current.date,
        value: current.value,
        delta: delta,
        deltaPercent: deltaPercent,
        isPositive: isPositive,
        type: pointType,
        previousValue: previous.value
      });
    }
    
    // If only one data point, mark it as 'end' as well
    if (waterfall.length === 1) {
      waterfall[0].type = 'end';
    }
    
    return waterfall;
  };

  // Get waterfall data (computed from chartData and factor kpi_type)
  const getWaterfallData = (): WaterfallDataPoint[] => {
    if (!chartData || chartData.length === 0) return [];
    
    try {
      // Get KPI type from chartDataResponse or selectedFactor
      const kpiType = chartDataResponse?.factor?.kpi_type || selectedFactor?.kpi_type || 'increase';
      
      return transformToWaterfallData(chartData, kpiType, displayMode);
    } catch (error) {
      console.error('Error transforming waterfall data:', error);
      return [];
    }
  };

  // Get waterfall chart data with cumulative values for Recharts
  // Waterfall chart shows: start bar (0 to first value), change bars (delta from previous), end bar (final cumulative)
  const getWaterfallChartData = () => {
    try {
      const waterfallData = getWaterfallData();
      if (waterfallData.length === 0) return [];
      
      const chartData: any[] = [];
      let cumulativeValue = 0;
    
    waterfallData.forEach((point, index) => {
      if (point.type === 'start') {
        // Starting point: bar from 0 to value
        chartData.push({
          label: point.label,
          start: 0,
          end: point.value,
          value: point.value,
          delta: 0,
          deltaValue: point.value, // For dataKey in Recharts (bar height)
          deltaPercent: 0,
          isPositive: true,
          type: 'start',
          previousValue: 0,
          cumulative: point.value
        });
        cumulativeValue = point.value;
      } else if (point.type === 'gap' || point.isGap) {
        // Gap marker: show as a thin dashed line or placeholder
        // Use a very small value for visualization
        chartData.push({
          label: point.gapLabel || point.label,
          start: cumulativeValue,
          end: cumulativeValue,
          value: cumulativeValue, // Keep same value (no change)
          delta: 0,
          deltaValue: 0.01, // Very small value to show as a line
          deltaPercent: 0,
          isPositive: true,
          type: 'gap',
          previousValue: cumulativeValue,
          cumulative: cumulativeValue,
          isGap: true,
          gapLabel: point.gapLabel || point.label
        });
        // Don't update cumulativeValue for gaps
      } else if (point.type === 'change' || point.type === 'end') {
        // Change point: bar shows the delta (change amount)
        // Bar starts at cumulative value, ends at new cumulative value
        const delta = point.delta || 0;
        const startValue = cumulativeValue;
        const endValue = cumulativeValue + delta; // New cumulative value
        
        chartData.push({
          label: point.label,
          start: startValue,
          end: endValue,
          value: point.value, // Final value
          delta: delta,
          deltaValue: delta, // For dataKey in Recharts
          deltaPercent: point.deltaPercent || 0,
          isPositive: point.isPositive,
          type: point.type,
          previousValue: point.previousValue || startValue,
          cumulative: endValue,
          isGap: false
        });
        cumulativeValue = endValue;
      }
    });
    
      return chartData;
    } catch (error) {
      console.error('Error generating waterfall chart data:', error);
      return [];
    }
  };

  // Get waterfall bar color
  const getWaterfallBarColor = (entry: any) => {
    if (entry.type === 'gap' || entry.isGap) {
      return '#9ca3af'; // Gray for gaps
    }
    if (entry.type === 'start' || entry.type === 'end') {
      return '#3b82f6'; // Blue for start/end
    }
    return entry.isPositive ? '#10b981' : '#ef4444'; // Green/Red based on change
  };

  // Format delta label for waterfall chart
  const formatDeltaLabel = (entry: any, includePercent: boolean = true) => {
    if (entry.type === 'start') return '';
    if (entry.delta === undefined || entry.delta === null) return '';
    
    const sign = entry.delta >= 0 ? '+' : '';
    const absoluteValue = Math.abs(entry.delta);
    
    // Format absolute change
    const absoluteStr = `${sign}${absoluteValue.toFixed(2)}`;
    
    // Format percentage change
    let percentStr = '';
    if (includePercent && entry.deltaPercent !== undefined) {
      if (isFinite(entry.deltaPercent)) {
        const percentSign = entry.deltaPercent >= 0 ? '+' : '';
        percentStr = ` (${percentSign}${entry.deltaPercent.toFixed(1)}%)`;
      } else if (entry.deltaPercent === Infinity || entry.deltaPercent === -Infinity) {
        percentStr = ' (∞%)';
      }
    }
    
    return `${absoluteStr}${percentStr}`;
  };

  // Format number with appropriate precision
  const formatNumber = (value: number, decimals: number = 2): string => {
    if (value === null || value === undefined || !isFinite(value)) return 'N/A';
    return value.toFixed(decimals);
  };

  // Custom tooltip for waterfall chart
  const WaterfallTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const isStart = data.type === 'start';
      const isEnd = data.type === 'end';
      const isGap = data.type === 'gap' || data.isGap;
      
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-xl min-w-[200px]">
          {/* Period Header */}
          <div className="mb-3 pb-2 border-b border-gray-200">
            <p className="font-semibold text-gray-900 text-sm">{`Period: ${label}`}</p>
            {isStart && (
              <p className="text-xs text-gray-500 mt-1">Baseline (Starting Point)</p>
            )}
            {isEnd && (
              <p className="text-xs text-gray-500 mt-1">Final Value</p>
            )}
            {isGap && (
              <p className="text-xs text-gray-500 mt-1">Data Gap</p>
            )}
          </div>
          
          {isGap ? (
            // Gap tooltip
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Status:</span>
                <span className="text-sm font-semibold text-gray-500">
                  No data available
                </span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <span className="text-sm text-gray-600">Last Value:</span>
                <span className="text-sm font-semibold text-gray-900">
                  {formatNumber(data.previousValue || data.value)}
                </span>
              </div>
              <div className="pt-2 border-t border-gray-100">
                <p className="text-xs text-gray-500 italic">
                  This period has no data points. The chart continues from the previous value.
                </p>
              </div>
            </div>
          ) : isStart ? (
            // Start point tooltip
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Starting Value:</span>
                <span className="text-sm font-semibold text-blue-600">
                  {formatNumber(data.value)}
                </span>
              </div>
            </div>
          ) : (
            // Change point tooltip
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Previous:</span>
                <span className="text-sm font-semibold text-gray-900">
                  {formatNumber(data.previousValue)}
                </span>
              </div>
              
              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <span className="text-sm text-gray-600">Change:</span>
                <span className={`text-sm font-bold ${
                  data.isPositive ? 'text-green-600' : 'text-red-600'
                }`}>
                  {formatDeltaLabel(data, true)}
                </span>
              </div>
              
              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <span className="text-sm text-gray-600">New:</span>
                <span className="text-sm font-semibold text-blue-600">
                  {formatNumber(data.value)}
                </span>
              </div>
              
              {/* Visual indicator */}
              <div className="pt-2 border-t border-gray-100">
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded ${
                    data.isPositive ? 'bg-green-500' : 'bg-red-500'
                  }`}></div>
                  <span className="text-xs text-gray-500">
                    {data.isPositive 
                      ? (selectedFactor?.kpi_type === 'increase' 
                          ? 'Improvement (↑)' 
                          : 'Improvement (↓)')
                      : (selectedFactor?.kpi_type === 'increase'
                          ? 'Decline (↓)'
                          : 'Decline (↑)')
                    }
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  // Custom bar color based on target achievement
  const getBarColor = (entry: ChartData) => {
    return entry.isOnTarget ? '#10b981' : '#ef4444'; // green or red
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const isAggregated = indicatorFrequency && displayMode !== indicatorFrequency;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium">{`${indicatorFrequency === 'daily' ? 'Date' : 'Period'}: ${label}`}</p>
          <p className="text-blue-600">
            {`Value: ${payload[0].value}`}
            {isAggregated && (
              <span className="ml-2 text-xs text-gray-500">
                ({displayMode} average)
              </span>
            )}
          </p>
          {selectedFactor?.target_value && (
            <p className="text-gray-600">{`Target: ${selectedFactor.target_value}`}</p>
          )}
          {isAggregated && data.notes && data.notes.includes('Average of') && (
            <p className="text-xs text-gray-400 mt-1 italic">{data.notes}</p>
          )}
          {!isAggregated && data.notes && <p className="text-sm text-gray-500 mt-1">{data.notes}</p>}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 border border-green-200">
          <div className="flex items-center space-x-3">
            <div className="bg-green-600 rounded-lg p-2 animate-pulse">
              <TrendingUpIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-green-900">Indicator Trend</h3>
              <p className="text-green-700 text-sm">
                Loading charts...
                {hasIndicatorFrequency && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                    Input frequency: {indicatorFrequency.charAt(0).toUpperCase() + indicatorFrequency.slice(1)}
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-1/4"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* <AlertComponent /> */}
      
      {/* Header */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 border border-green-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-green-600 rounded-lg p-2">
              <TrendingUpIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-semibold text-green-900">Indicator Trend</h3>
                {hasIndicatorFrequency && (
                  <div className="group relative">
                    <InfoIcon className="h-4 w-4 text-green-400 hover:text-green-600 cursor-help" />
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                      <p className="font-semibold mb-1">Indicator Frequency</p>
                      <p className="mb-2">
                        {indicatorFrequency === 'daily' && 'Enter data by specific dates (e.g., 2025-10-24)'}
                        {indicatorFrequency === 'weekly' && 'Enter data by week numbers (e.g., Week 48, 2025)'}
                        {indicatorFrequency === 'monthly' && 'Enter data by month names (e.g., October 2025)'}
                      </p>
                      <p className="text-gray-300">
                        Data is stored with all time attributes for flexible display.
                      </p>
                      <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                    </div>
                  </div>
                )}
              </div>
              <p className="text-green-700 text-sm">
                Track KPI performance over time
                {hasIndicatorFrequency && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                    Input frequency: {indicatorFrequency.charAt(0).toUpperCase() + indicatorFrequency.slice(1)}
                  </span>
                )}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Display Mode Selector */}
            {hasIndicatorFrequency && allowedModes.length > 1 && (
              <div className="flex items-center space-x-2">
                <div className="flex items-center space-x-1">
                  <span className="text-sm text-gray-600">Display:</span>
                  <div className="group relative">
                    <InfoIcon className="h-3 w-3 text-green-400 hover:text-green-600 cursor-help" />
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-72 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                      <p className="font-semibold mb-2">Display Modes</p>
                      <ul className="space-y-1 text-gray-300">
                        <li>• <strong>Daily:</strong> Shows individual data points by date</li>
                        <li>• <strong>Weekly:</strong> Shows weekly averages (aggregated from daily data)</li>
                        <li>• <strong>Monthly:</strong> Shows monthly averages (aggregated from daily/weekly data)</li>
                      </ul>
                      <p className="mt-2 text-gray-400 text-xs">
                        Available modes depend on your input frequency.
                      </p>
                      <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                    </div>
                  </div>
                </div>
                <div className="inline-flex rounded-md shadow-sm" role="group">
                  {allowedModes.map((mode) => {
                    const isActive = displayMode === mode;
                    const isAggregated = indicatorFrequency && mode !== indicatorFrequency;
                    return (
                      <button
                        key={mode}
                        onClick={() => setDisplayMode(mode)}
                        title={isAggregated ? `Showing ${mode} averages (aggregated from ${indicatorFrequency} data)` : `Showing ${mode} data points`}
                        className={`px-4 py-2 text-sm font-medium border ${
                          isActive
                            ? 'bg-green-600 text-white border-green-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        } ${
                          mode === allowedModes[0] ? 'rounded-l-md' : ''
                        } ${
                          mode === allowedModes[allowedModes.length - 1] ? 'rounded-r-md' : '-ml-px'
                        }`}
                      >
                        {mode.charAt(0).toUpperCase() + mode.slice(1)}
                        {isAggregated && <span className="ml-1 text-xs">(avg)</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Add Data Point Button */}
            {selectedFactor && canEdit && (
              <button
                onClick={() => {
                  setFormData({ 
                    date: '', 
                    week_number: '',
                    year: new Date().getFullYear().toString(),
                    month_name: '',
                    value: '', 
                    notes: '' 
                  });
                  setEditingPoint(null);
                  setShowAddForm(true);
                }}
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Data Point
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* No Factors State */}
      {factors.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-8">
          <div className="text-center">
            <TargetIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Factors Found</h3>
            <p className="text-gray-500 mb-4">
              Charts are automatically created based on factors from outcomes in Components 2 & 3.
            </p>
            <p className="text-sm text-gray-400">
              Add outcomes with factors in Problem Statement or Target Condition to see charts here.
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Factor Selector */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-medium text-gray-900">Select Factor to Track</h4>
              <span className="text-sm text-gray-500">{factors.length} Factor(s) available</span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {factors.map((factor) => (
                <button
                  key={factor.id}
                  onClick={() => setSelectedFactor(factor)}
                  className={`factor-selector-btn p-4 rounded-lg border-2 text-left transition-colors ${
                    selectedFactor?.id === factor.id
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="mb-2">
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {(factor as any).outcome_name}
                    </span>
                  </div>
                  <h5 className="font-medium text-gray-900 mb-1">{factor.name}</h5>
                  <p className="text-sm text-gray-600 mb-2">{factor.description}</p>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">
                      Target: {factor.target_value || 'Not set'}
                    </span>
                    <span className={`px-2 py-1 rounded ${
                      factor.kpi_type === 'increase' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-orange-100 text-orange-800'
                    }`}>
                      {factor.kpi_type === 'increase' ? '↑' : '↓'} {factor.kpi_type}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Chart Section */}
          {selectedFactor && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="mb-1">
                    <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {(selectedFactor as any).outcome_name}
                    </span>
                  </div>
                  <h4 className="text-lg font-medium text-gray-900">{selectedFactor.name}</h4>
                  <p className="text-sm text-gray-600">{selectedFactor.description}</p>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">Target Value</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {selectedFactor.target_value || 'Not set'}
                  </div>
                </div>
              </div>

              {chartData.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Points Yet</h3>
                  <p className="text-gray-500 mb-4">
                    Start tracking this KPI by adding your first measurement.
                  </p>
                  {canEdit && (
                  <button
                    onClick={() => {
                      setFormData({ 
                        date: '', 
                        week_number: '',
                        year: new Date().getFullYear().toString(),
                        month_name: '',
                        value: '', 
                        notes: '' 
                      });
                      setEditingPoint(null);
                      setShowAddForm(true);
                    }}
                    className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Add First Data Point
                  </button>
                  )}
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Chart */}
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="date" 
                          tick={{ fontSize: 12 }}
                          angle={-45}
                          textAnchor="end"
                          height={60}
                        />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend 
                          content={({ payload }) => {
                            const isAggregated = indicatorFrequency && displayMode !== indicatorFrequency;
                            const legendText = isAggregated 
                              ? `${displayMode.charAt(0).toUpperCase() + displayMode.slice(1)} Average (from ${indicatorFrequency} data)`
                              : selectedFactor.name;
                            return (
                              <div className="flex justify-center mt-4">
                                <div className="flex items-center space-x-2">
                                  <div className="w-4 h-4 bg-green-600 rounded"></div>
                                  <span className="text-sm text-gray-600">{legendText}</span>
                                  {isAggregated && (
                                    <span className="text-xs text-gray-500">(aggregated)</span>
                                  )}
                                </div>
                              </div>
                            );
                          }}
                        />
                        
                        {/* Target line */}
                        {selectedFactor.target_value && (
                          <ReferenceLine 
                            y={parseFloat(selectedFactor.target_value.toString())} 
                            stroke="#6b7280" 
                            strokeDasharray="5 5"
                            label="Target"
                          />
                        )}
                        
                        <Bar 
                          dataKey="value" 
                          name={indicatorFrequency && displayMode !== indicatorFrequency
                            ? `${displayMode.charAt(0).toUpperCase() + displayMode.slice(1)} Average`
                            : selectedFactor.name}
                        >
                          {chartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={getBarColor(entry)} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Waterfall Chart */}
                  {chartData.length === 0 ? (
                    // No data state - already handled by parent empty state
                    null
                  ) : chartData.length === 1 ? (
                    // Single data point state
                    <div className="mt-8">
                      <div className="mb-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <h4 className="text-lg font-medium text-gray-900">Change Analysis (Waterfall)</h4>
                          <div className="group relative">
                            <InfoIcon className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help" />
                            <div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                              <p className="font-semibold mb-1">Waterfall Chart</p>
                              <p className="text-gray-300">
                                Shows the change between consecutive data points. Requires at least 2 data points to display changes.
                              </p>
                            </div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-500">
                          Shows the change between consecutive data points
                        </p>
                      </div>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                        <InfoIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h5 className="text-lg font-medium text-gray-900 mb-2">Insufficient Data</h5>
                        <p className="text-gray-500 mb-4">
                          The waterfall chart requires at least 2 data points to show changes between periods.
                        </p>
                        <p className="text-sm text-gray-400">
                          Add another data point to see the change analysis.
                        </p>
                      </div>
                    </div>
                  ) : (
                    // Waterfall chart with 2+ data points
                    <div className="mt-8">
                      <div className="mb-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <h4 className="text-lg font-medium text-gray-900">Change Analysis (Waterfall)</h4>
                          <div className="group relative">
                            <InfoIcon className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help" />
                            <div className="absolute left-0 bottom-full mb-2 w-72 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                              <p className="font-semibold mb-2">Waterfall Chart</p>
                              <p className="text-gray-300 mb-2">
                                Visualizes changes between consecutive data points:
                              </p>
                              <ul className="list-disc list-inside text-gray-300 space-y-1">
                                <li>Blue bars: Starting and ending values</li>
                                <li>Green bars: Positive changes (improvements)</li>
                                <li>Red bars: Negative changes (declines)</li>
                                <li>Gray dashed lines: Missing data periods</li>
                              </ul>
                              <p className="text-gray-300 mt-2 text-xs">
                                Changes are color-coded based on the factor type (increase/decrease).
                              </p>
                            </div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-500">
                          Shows the change between consecutive data points
                        </p>
                      </div>
                      <div className="h-80 w-full">
                        {(() => {
                          const waterfallChartData = getWaterfallChartData();
                          if (waterfallChartData.length === 0) {
                            return (
                              <div className="flex items-center justify-center h-full bg-gray-50 border border-gray-200 rounded-lg">
                                <div className="text-center p-4">
                                  <InfoIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                                  <p className="text-sm text-gray-500">Unable to generate waterfall chart</p>
                                  <p className="text-xs text-gray-400 mt-1">Please check your data</p>
                                </div>
                              </div>
                            );
                          }
                          return (
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart 
                                data={waterfallChartData} 
                                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                              >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="label" 
                              tick={{ fontSize: 12 }}
                              angle={-45}
                              textAnchor="end"
                              height={80}
                            />
                            <YAxis tick={{ fontSize: 12 }} />
                            <Tooltip content={<WaterfallTooltip />} />
                            <Legend 
                              content={() => (
                                <div className="flex justify-center mt-4">
                                  <div className="flex items-center space-x-4">
                                    <div className="flex items-center space-x-2">
                                      <div className="w-4 h-4 bg-blue-600 rounded"></div>
                                      <span className="text-sm text-gray-600">Start/End</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <div className="w-4 h-4 bg-green-600 rounded"></div>
                                      <span className="text-sm text-gray-600">Positive Change</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <div className="w-4 h-4 bg-red-600 rounded"></div>
                                      <span className="text-sm text-gray-600">Negative Change</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <div className="w-4 h-4 border-2 border-gray-400 border-dashed rounded"></div>
                                      <span className="text-sm text-gray-600">Data Gap</span>
                                    </div>
                                  </div>
                                </div>
                              )}
                            />
                            <Bar 
                              dataKey="deltaValue"
                              name="Change"
                              shape={(props: any) => {
                                const { x, y, width, height, payload } = props;
                                const start = payload.start || 0;
                                const end = payload.end || 0;
                                const delta = end - start;
                                
                                // Calculate actual Y position based on start value
                                // Recharts gives us y based on dataKey (delta), but we need to position at start
                                // We need to calculate the Y position for the start value
                                // This is a simplified approach - in a real waterfall, we'd need Y-axis scale
                                const actualY = y; // This will be adjusted by Recharts based on delta
                                const actualHeight = Math.abs(height);
                                
                                return (
                                  <g>
                                    {/* Connecting line from previous bar end to current bar start */}
                                    {payload.type !== 'start' && (
                                      <line
                                        x1={x - width}
                                        y1={y + actualHeight}
                                        x2={x}
                                        y2={y + actualHeight}
                                        stroke="#94a3b8"
                                        strokeWidth={2}
                                        strokeDasharray="3 3"
                                      />
                                    )}
                                    {/* Bar or Gap visualization */}
                                    {payload.type === 'gap' || payload.isGap ? (
                                      // Gap: show as dashed horizontal line
                                      <g>
                                        <line
                                          x1={x}
                                          y1={actualY + actualHeight / 2}
                                          x2={x + width}
                                          y2={actualY + actualHeight / 2}
                                          stroke="#9ca3af"
                                          strokeWidth={2}
                                          strokeDasharray="5 5"
                                        />
                                        {/* Gap label */}
                                        <text
                                          x={x + width / 2}
                                          y={actualY + actualHeight / 2 - 8}
                                          textAnchor="middle"
                                          fontSize={9}
                                          fill="#6b7280"
                                          fontWeight="600"
                                        >
                                          {payload.gapLabel || 'Gap'}
                                        </text>
                                      </g>
                                    ) : (
                                      // Regular bar
                                      <rect
                                        x={x}
                                        y={actualY}
                                        width={width}
                                        height={actualHeight}
                                        fill={getWaterfallBarColor(payload)}
                                        opacity={0.8}
                                      />
                                    )}
                                    {/* Label showing delta for change bars */}
                                    {payload.type === 'change' && payload.delta !== undefined && payload.delta !== 0 && (
                                      <g>
                                        {/* Background rectangle for better readability */}
                                        <rect
                                          x={x + width / 2 - 40}
                                          y={delta >= 0 ? actualY - 20 : actualY + actualHeight + 5}
                                          width={80}
                                          height={16}
                                          fill="white"
                                          fillOpacity={0.95}
                                          stroke={payload.isPositive ? '#059669' : '#dc2626'}
                                          strokeWidth={1.5}
                                          rx={3}
                                        />
                                        {/* Delta label text */}
                                        <text
                                          x={x + width / 2}
                                          y={delta >= 0 ? actualY - 8 : actualY + actualHeight + 16}
                                          textAnchor="middle"
                                          fontSize={10}
                                          fill={payload.isPositive ? '#059669' : '#dc2626'}
                                          fontWeight="bold"
                                        >
                                          {formatDeltaLabel(payload, true)}
                                        </text>
                                      </g>
                                    )}
                                    {/* Value label on bar for start/end bars */}
                                    {(payload.type === 'start' || payload.type === 'end') && (
                                      <g>
                                        {/* Background for better contrast */}
                                        <rect
                                          x={x + width / 2 - 30}
                                          y={actualY + actualHeight / 2 - 9}
                                          width={60}
                                          height={18}
                                          fill="rgba(0, 0, 0, 0.7)"
                                          rx={3}
                                        />
                                        <text
                                          x={x + width / 2}
                                          y={actualY + actualHeight / 2 + 5}
                                          textAnchor="middle"
                                          fontSize={11}
                                          fill="white"
                                          fontWeight="bold"
                                        >
                                          {formatNumber(payload.value, 1)}
                                        </text>
                                      </g>
                                    )}
                                    {/* Small delta label for end bar if it has a change */}
                                    {payload.type === 'end' && payload.delta !== undefined && payload.delta !== 0 && (
                                      <g>
                                        <rect
                                          x={x + width / 2 - 35}
                                          y={delta >= 0 ? actualY - 18 : actualY + actualHeight + 3}
                                          width={70}
                                          height={14}
                                          fill="white"
                                          fillOpacity={0.9}
                                          stroke={payload.isPositive ? '#059669' : '#dc2626'}
                                          strokeWidth={1}
                                          rx={2}
                                        />
                                        <text
                                          x={x + width / 2}
                                          y={delta >= 0 ? actualY - 7 : actualY + actualHeight + 13}
                                          textAnchor="middle"
                                          fontSize={9}
                                          fill={payload.isPositive ? '#059669' : '#dc2626'}
                                          fontWeight="600"
                                        >
                                          {formatDeltaLabel(payload, false)}
                                        </text>
                                      </g>
                                    )}
                                  </g>
                                );
                              }}
                            >
                              {waterfallChartData.map((entry, index) => (
                                <Cell key={`waterfall-cell-${index}`} fill={getWaterfallBarColor(entry)} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                          );
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Data Points Table */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Week
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Month
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Year
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Value
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Target Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Notes
                          </th>
                          {canEdit && (
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {dataPoints.map((point) => (
                          <tr key={point.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {point.date || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {point.week_number ? `Week ${point.week_number}` : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {point.month_name || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {point.year || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {point.value}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                point.is_on_target 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {point.is_on_target ? '✓ On Target' : '✗ Off Target'}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900">
                              {point.notes || '-'}
                            </td>
                            {canEdit && (
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <button
                                onClick={() => handleEdit(point)}
                                className="text-blue-600 hover:text-blue-900 mr-3"
                              >
                                <EditIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(point)}
                                className="text-red-600 hover:text-red-900"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Add/Edit Data Point Modal */}
      {showAddForm && canEdit && selectedFactor && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingPoint ? 'Edit Data Point' : 'Add Data Point'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Daily Mode: Date Picker */}
                {indicatorFrequency === 'daily' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.date}
                      onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                )}

                {/* Weekly Mode: Week Number + Year */}
                {indicatorFrequency === 'weekly' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Week Number (1-52) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="52"
                        required
                        value={formData.week_number}
                        onChange={(e) => setFormData(prev => ({ ...prev, week_number: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="1-52"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Year <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        min="2000"
                        max="2100"
                        required
                        value={formData.year}
                        onChange={(e) => setFormData(prev => ({ ...prev, year: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                  </>
                )}

                {/* Monthly Mode: Month + Year */}
                {indicatorFrequency === 'monthly' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Month <span className="text-red-500">*</span>
                      </label>
                      <select
                        required
                        value={formData.month_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, month_name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="">Select month</option>
                        {months.map(month => (
                          <option key={month} value={month}>{month}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Year <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        min="2000"
                        max="2100"
                        required
                        value={formData.year}
                        onChange={(e) => setFormData(prev => ({ ...prev, year: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                  </>
                )}
                
                {/* Value */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Value <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={formData.value}
                    onChange={(e) => setFormData(prev => ({ ...prev, value: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Enter measurement value"
                  />
                </div>
                
                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    rows={3}
                    placeholder="Add context or notes about this measurement"
                  />
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddForm(false);
                      setEditingPoint(null);
                      setFormData({ 
                        date: '', 
                        week_number: '',
                        year: new Date().getFullYear().toString(),
                        month_name: '',
                        value: '', 
                        notes: '' 
                      });
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50"
                  >
                    {submitting ? 'Saving...' : editingPoint ? 'Update' : 'Add'} Data Point
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Component07_IndicatorTrend;

